import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Badge, Button, Modal } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { formatDistanceToNow } from 'date-fns'
import { toast } from 'react-toastify'
import { MoreHorizontal, ChevronDown, ArrowRightLeft, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shadcn/ui/dialog'
import { Button as UiButton } from '@/features/shadcn/ui/button'
import { cn } from '@/features/utils'
import {
  removeTeamAccount,
  updateSeatAssignment,
  fetchTeamAccounts,
  selectTeamAccounts,
  selectTeamAccountsStatus,
} from '../../../settingsSlice'
import { transferOrganizationOwnership } from '../../../../../features/organizations/organizationsSlice'
import TeamMemberPermissionsPanel from './TeamMemberPermissionsPanel'
import MemberPermissionsBottomSheet from './MemberPermissionsBottomSheet'
import {
  mergeAccessPermissionWithCanonical,
  countMemberPageAccess,
  canManageAllTeamIntegrations,
} from '../teamPagePermissions'
import { countToggleableToolAccess } from '../teamMemberPermissionFormUtils'
import { useIsMobile } from '../../../../../features/hooks/use-mobile'

const StatusBadge = ({ color, label }) => {
  return (
    <Badge pill bg={color}>
      {label}
    </Badge>
  )
}

const PageAccessPill = ({ enabled, total, muted }: { enabled: number; total: number; muted?: boolean }) => (
  <span
    className={`tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium ${
      muted
        ? 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-500'
        : 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-700'
    }`}
  >
    {enabled} of {total} pages
  </span>
)

const AiToolsPill = ({ enabled, total, muted }: { enabled: number; total: number; muted?: boolean }) => (
  <span
    className={`tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium ${
      muted
        ? 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-500'
        : 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-700'
    }`}
  >
    {enabled} of {total} tools
  </span>
)

const IntegrationManagePill = ({ allowed, muted }: { allowed: boolean; muted?: boolean }) => (
  <span
    className={`tw-inline-flex tw-max-w-full tw-items-center tw-rounded-full tw-border tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium ${
      muted
        ? 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-500'
        : allowed
          ? 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-700'
          : 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-500'
    }`}
    title="Manage connections: Yes means this member may disconnect or remove any team connection. Everyone may run backfills; without Yes they may still disconnect connections they added themselves."
  >
    {allowed ? 'Yes' : 'No'}
  </span>
)

const destructiveBtnClass =
  'tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-rose-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-rose-700 tw-shadow-sm tw-transition tw-duration-150 hover:tw-border-rose-300 hover:tw-bg-rose-50 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-rose-300/50 active:tw-scale-[0.98]'

function orgRoleLabel(role: string | undefined | null, inOrgContext: boolean): string {
  if (!inOrgContext) return 'Member'
  if (role === 'owner') return 'Owner'
  if (role === 'admin') return 'Organization Manager'
  return 'Member'
}

function resolveMemberOrgRole(
  email: string,
  inOrgContext: boolean,
  rolesByEmail: Record<string, string>
): string {
  if (!inOrgContext) return 'Member'
  return orgRoleLabel(rolesByEmail[email.toLowerCase()] || 'member', true)
}

function OrgMemberRoleCell({
  email,
  inOrgContext = false,
  isOrgOwner = false,
  organizationId,
  rolesByEmail = {},
  onTransfer,
}: {
  email: string
  inOrgContext?: boolean
  isOrgOwner?: boolean
  organizationId?: string
  rolesByEmail?: Record<string, string>
  onTransfer: (email: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [menuRect, setMenuRect] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const roleKey = inOrgContext ? rolesByEmail[email.toLowerCase()] || 'member' : 'member'
  const label = orgRoleLabel(roleKey, inOrgContext)
  const canManage =
    inOrgContext && isOrgOwner && roleKey !== 'owner' && !!organizationId

  const updateMenuRect = () => {
    const button = buttonRef.current
    if (!button) return
    const rect = button.getBoundingClientRect()
    setMenuRect({ top: rect.bottom + 4, left: rect.left })
  }

  useEffect(() => {
    if (!open) {
      setMenuRect(null)
      return
    }
    updateMenuRect()
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const onReposition = () => updateMenuRect()
    document.addEventListener('mousedown', onDoc)
    window.addEventListener('scroll', onReposition, true)
    window.addEventListener('resize', onReposition)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      window.removeEventListener('scroll', onReposition, true)
      window.removeEventListener('resize', onReposition)
    }
  }, [open])

  if (!canManage) {
    return <span className="tw-text-slate-800">{label}</span>
  }

  return (
    <div className="tw-inline-flex tw-items-center tw-gap-2">
      <span className="tw-text-slate-800">{label}</span>
      <button
        ref={buttonRef}
        type="button"
        className={`tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 tw-transition-colors tw-duration-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-slate-400/40 ${
          open ? 'tw-border-slate-300 tw-bg-slate-50' : ''
        }`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Organization actions for ${email}`}
        onClick={() => {
          setOpen((v) => {
            const next = !v
            if (next && buttonRef.current) {
              const rect = buttonRef.current.getBoundingClientRect()
              setMenuRect({ top: rect.bottom + 4, left: rect.left })
            }
            return next
          })
        }}
      >
        <ChevronDown
          className={`tw-h-3.5 tw-w-3.5 tw-transition-transform tw-duration-200 ${
            open ? '-tw-rotate-180' : ''
          }`}
          strokeWidth={2}
        />
      </button>
      {open && menuRect
        ? createPortal(
            <div
              ref={menuRef}
              className="tw-fixed tw-z-[110] tw-min-w-[12rem] tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-py-1 tw-shadow-lg tw-ring-1 tw-ring-black/5"
              style={{ top: menuRect.top, left: menuRect.left }}
              role="menu"
            >
              <button
                type="button"
                role="menuitem"
                className="tw-flex tw-min-h-[36px] tw-w-full tw-items-center tw-px-3 tw-text-left tw-text-sm tw-text-amber-800 hover:tw-bg-amber-50"
                onClick={() => {
                  setOpen(false)
                  onTransfer(email)
                }}
              >
                Transfer ownership…
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  )
}

const RemoveButton = ({ email, label, className = '' }: { email: string; label: string; className?: string }) => {
  const dispatch = useDispatch()

  const handleClick = async () => {
    try {
      await dispatch(removeTeamAccount(email)).unwrap()
      toast.success(`Account (${email}) successfully removed`)
    } catch (error) {
      console.error('TeamAccountsTable RemoveButton', error)
      toast.error(`Something went wrong removing the account: ${email}`)
    }
  }

  return (
    <button type="button" className={`${destructiveBtnClass} ${className}`.trim()} onClick={handleClick}>
      {label}
    </button>
  )
}

const SeatButton = ({ email, seatAssigned, ownerEmail }) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  const handleToggle = async () => {
    setLoading(true)
    try {
      await dispatch(
        updateSeatAssignment({ shared_with: email, seatAssigned: !seatAssigned, owner: ownerEmail })
      ).unwrap()
      toast.success(`Seat ${seatAssigned ? 'unassigned from' : 'assigned to'} ${email}`)
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to update seat assignment'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      className="btn-sm"
      variant={seatAssigned ? 'outline-secondary' : 'success'}
      onClick={handleToggle}
      disabled={loading}
    >
      {seatAssigned ? 'Unassign seat' : 'Assign seat'}
    </Button>
  )
}

function UserCell({
  initial,
  title,
  subtitle,
  badge,
}: {
  initial: string
  title: string
  subtitle: string
  badge?: React.ReactNode
}) {
  return (
    <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
      <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-100 tw-text-sm tw-font-semibold tw-text-indigo-800">
        {initial}
      </div>
      <div className="tw-min-w-0 tw-flex-1">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-break-words tw-font-semibold tw-text-slate-900">{title}</span>
          {badge}
        </div>
        <div className="tw-truncate tw-text-sm tw-text-slate-500">{subtitle}</div>
      </div>
    </div>
  )
}

function MobileMemberOverflowMenu({
  email,
  isEntrepreneur,
  seatAssigned,
  ownerEmail,
  onRevoke,
  inOrgContext = false,
  isOrgOwner = false,
  organizationId,
  rolesByEmail = {},
  onTransfer,
}: {
  email: string
  isEntrepreneur: boolean
  seatAssigned: boolean
  ownerEmail: string
  onRevoke: () => void
  inOrgContext?: boolean
  isOrgOwner?: boolean
  organizationId?: string
  rolesByEmail?: Record<string, string>
  onTransfer?: (email: string) => void
}) {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [seatLoading, setSeatLoading] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const roleKey = inOrgContext ? rolesByEmail[email.toLowerCase()] || 'member' : 'member'
  const canManageOrgRole =
    inOrgContext && isOrgOwner && roleKey !== 'owner' && !!organizationId

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const toggleSeat = async () => {
    setSeatLoading(true)
    try {
      await dispatch(
        updateSeatAssignment({ shared_with: email, seatAssigned: !seatAssigned, owner: ownerEmail })
      ).unwrap()
      toast.success(`Seat ${seatAssigned ? 'unassigned from' : 'assigned to'} ${email}`)
      setOpen(false)
    } catch (error) {
      const message = typeof error === 'string' ? error : (error as Error)?.message || 'Failed to update seat assignment'
      toast.error(message)
    } finally {
      setSeatLoading(false)
    }
  }

  return (
    <div ref={wrapRef} className="tw-relative tw-shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-lg tw-text-slate-600 tw-transition-colors hover:tw-bg-slate-100 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-slate-400/40"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Member actions"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal className="tw-h-5 tw-w-5" strokeWidth={2} />
      </button>
      {open ? (
        <div
          className="tw-absolute tw-right-0 tw-top-full tw-z-20 tw-mt-1 tw-min-w-[12rem] tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-py-1 tw-shadow-lg tw-ring-1 tw-ring-black/5"
          role="menu"
        >
          {isEntrepreneur ? (
            <button
              type="button"
              role="menuitem"
              disabled={seatLoading}
              className="tw-flex tw-min-h-[44px] tw-w-full tw-items-center tw-px-4 tw-text-left tw-text-sm tw-text-slate-800 hover:tw-bg-slate-50 disabled:tw-opacity-50"
              onClick={() => toggleSeat()}
            >
              {seatAssigned ? 'Unassign seat' : 'Assign seat'}
            </button>
          ) : null}
          {canManageOrgRole ? (
            <button
              type="button"
              role="menuitem"
              className="tw-flex tw-min-h-[44px] tw-w-full tw-items-center tw-px-4 tw-text-left tw-text-sm tw-text-amber-800 hover:tw-bg-amber-50"
              onClick={() => {
                setOpen(false)
                onTransfer?.(email)
              }}
            >
              Transfer ownership…
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="tw-flex tw-min-h-[44px] tw-w-full tw-items-center tw-px-4 tw-text-left tw-text-sm tw-text-rose-700 hover:tw-bg-rose-50 disabled:tw-opacity-50"
            onClick={() => {
              setOpen(false)
              onRevoke()
            }}
          >
            Revoke access
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default function TeamAccountsTable({
  accounts,
  readOnly = false,
  isEntrepreneur = false,
  ownerEmail = '',
  billingOwnerEmail = '',
  ownerDisplayName = '',
  showAiToolsColumn = false,
  aiToolsFeatureEnabled = false,
  availableToolsCatalog = [],
  inOrgContext = false,
  isOrgOwner = false,
  organizationId,
  rolesByEmail = {},
  memberUidByEmail = {},
}) {
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const [revokeConfirmEmail, setRevokeConfirmEmail] = useState<string | null>(null)
  const [revokeLoading, setRevokeLoading] = useState(false)
  const [transferConfirmEmail, setTransferConfirmEmail] = useState<string | null>(null)
  const [transferLoading, setTransferLoading] = useState(false)

  const closeRevokeModal = () => {
    if (!revokeLoading) setRevokeConfirmEmail(null)
  }

  const closeTransferModal = () => {
    if (!transferLoading) setTransferConfirmEmail(null)
  }

  const handleConfirmTransfer = async () => {
    if (!transferConfirmEmail || !organizationId) return
    const memberUniqueUserId = memberUidByEmail[transferConfirmEmail.toLowerCase()]
    if (!memberUniqueUserId) {
      toast.error('Could not resolve organization member for transfer')
      return
    }
    setTransferLoading(true)
    try {
      await dispatch(
        transferOrganizationOwnership({
          organizationId,
          memberUniqueUserId,
        })
      ).unwrap()
      window.location.reload()
    } catch (error) {
      const message =
        typeof error === 'string'
          ? error
          : (error as Error)?.message || 'Failed to transfer organization ownership'
      toast.error(message)
      setTransferLoading(false)
    }
  }

  const handleConfirmRevoke = async () => {
    if (!revokeConfirmEmail) return
    setRevokeLoading(true)
    try {
      await dispatch(removeTeamAccount(revokeConfirmEmail)).unwrap()
      toast.success(`Account (${revokeConfirmEmail}) successfully removed`)
      setRevokeConfirmEmail(null)
    } catch (error) {
      console.error('TeamAccountsTable revoke confirm', error)
      toast.error(`Something went wrong removing the account: ${revokeConfirmEmail}`)
    } finally {
      setRevokeLoading(false)
    }
  }

  const [expandedEmail, setExpandedEmail] = useState<string | null>(null)

  const { acceptedMembers, pendingInvites } = useMemo(() => {
    const list = accounts || []
    return {
      acceptedMembers: list.filter((a) => a?.status === 'accepted'),
      pendingInvites: list.filter((a) => a?.status === 'invited'),
    }
  }, [accounts])

  const expandedAccount = useMemo(
    () => acceptedMembers.find((a) => a?.shared_with === expandedEmail) || null,
    [acceptedMembers, expandedEmail]
  )

  const teamAccounts = useSelector(selectTeamAccounts)
  const teamAccountsStatus = useSelector(selectTeamAccountsStatus)

  useEffect(() => {
    if (readOnly) return
    if (teamAccountsStatus === 'idle') {
      dispatch(fetchTeamAccounts())
    }
  }, [readOnly, teamAccountsStatus, dispatch])

  const teamOwnerEmail = billingOwnerEmail || ownerEmail
  const ownerInitial = (ownerDisplayName || ownerEmail || 'O').trim().charAt(0).toUpperCase()
  const ownerTitle = ownerDisplayName || ownerEmail || 'Owner'
  const ownerPageStats = countMemberPageAccess(null)
  const catalogLoaded = Array.isArray(availableToolsCatalog) && availableToolsCatalog.length > 0
  const ownerTools = useMemo(
    () => countToggleableToolAccess(mergeAccessPermissionWithCanonical(null), availableToolsCatalog),
    [availableToolsCatalog]
  )

  const activeColumns: string[] = readOnly
    ? ['User', 'Role']
    : ['User', 'Role', 'Page access', ...(showAiToolsColumn ? ['AI tools'] : []), 'Manage connections', '']

  const sheetOpen = Boolean(isMobile && !readOnly && expandedEmail && expandedAccount)

  return (
    <div className="tw-max-w-full tw-space-y-8 tw-overflow-x-hidden">
      <section className="tw-min-w-0">
        <h3 className="tw-mb-3 tw-px-4 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-slate-500 tw-uppercase md:tw-px-6">
          Active members
        </h3>

        {/* Desktop: unchanged table */}
        <div className="tw-hidden md:tw-block md:tw-px-6">
          <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-text-sm tw-shadow-sm">
            <table className="tw-w-full tw-table-auto tw-border-collapse">
              <thead className="tw-border-b tw-bg-slate-50 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-600">
                <tr>
                  {activeColumns.map((heading, i) => (
                    <th key={heading || `col-${i}`} className="tw-px-4 tw-py-3">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="tw-border-b tw-border-slate-100 tw-bg-white">
                  <td className="tw-px-4 tw-py-3 tw-align-middle">
                    <UserCell
                      initial={ownerInitial}
                      title={ownerTitle}
                      subtitle={ownerEmail}
                      badge={
                        <Badge pill bg="secondary" className="tw-text-[10px] tw-font-medium">
                          Owner
                        </Badge>
                      }
                    />
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-slate-800">Owner</td>
                  {!readOnly ? (
                    <>
                      <td className="tw-px-4 tw-py-3 tw-align-middle">
                        <PageAccessPill enabled={ownerPageStats.enabled} total={ownerPageStats.total} />
                      </td>
                      {showAiToolsColumn ? (
                        <td className="tw-px-4 tw-py-3 tw-align-middle">
                          {catalogLoaded ? (
                            <AiToolsPill enabled={ownerTools.enabled} total={ownerTools.total} />
                          ) : (
                            <span className="tw-text-slate-400">—</span>
                          )}
                        </td>
                      ) : null}
                      <td className="tw-px-4 tw-py-3 tw-align-middle">
                        <IntegrationManagePill allowed />
                      </td>
                      <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-right" />
                    </>
                  ) : null}
                </tr>

                {acceptedMembers.map((account) => {
                  if (!account) return null
                  const merged = mergeAccessPermissionWithCanonical(account.access_permission)
                  const pageStats = countMemberPageAccess(account.access_permission)
                  const toolStats = countToggleableToolAccess(merged, availableToolsCatalog)
                  const canManageIntegrations = canManageAllTeamIntegrations(account.access_permission)
                  const displayName = account.shared_with
                  const initial = displayName.trim().charAt(0).toUpperCase()
                  const open = !readOnly && expandedEmail === account.shared_with

                  return (
                    <React.Fragment key={account.shared_with}>
                      <tr className="tw-border-b tw-border-slate-100 tw-bg-white">
                        <td className="tw-px-4 tw-py-3 tw-align-middle">
                          <UserCell initial={initial} title={displayName} subtitle={displayName} />
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-align-middle">
                          <OrgMemberRoleCell
                            email={account.shared_with}
                            inOrgContext={inOrgContext}
                            isOrgOwner={isOrgOwner}
                            organizationId={organizationId}
                            rolesByEmail={rolesByEmail}
                            onTransfer={setTransferConfirmEmail}
                          />
                        </td>
                        {!readOnly ? (
                          <>
                            <td className="tw-px-4 tw-py-3 tw-align-middle">
                              <PageAccessPill enabled={pageStats.enabled} total={pageStats.total} />
                            </td>
                            {showAiToolsColumn ? (
                              <td className="tw-px-4 tw-py-3 tw-align-middle">
                                {catalogLoaded ? (
                                  <AiToolsPill enabled={toolStats.enabled} total={toolStats.total} />
                                ) : (
                                  <span className="tw-text-slate-400">—</span>
                                )}
                              </td>
                            ) : null}
                            <td className="tw-px-4 tw-py-3 tw-align-middle">
                              <IntegrationManagePill allowed={canManageIntegrations} />
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-align-middle">
                              <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                {isEntrepreneur ? (
                                  <SeatButton
                                    email={account.shared_with}
                                    seatAssigned={account.seat_assigned}
                                    ownerEmail={teamOwnerEmail}
                                  />
                                ) : null}
                                <button
                                  type="button"
                                  className={destructiveBtnClass}
                                  onClick={() => setRevokeConfirmEmail(account.shared_with)}
                                >
                                  Revoke
                                </button>
                                <button
                                  type="button"
                                  className="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 tw-transition-colors tw-duration-200 hover:tw-border-slate-300 hover:tw-bg-slate-50"
                                  aria-expanded={open}
                                  aria-label={open ? 'Collapse permissions' : 'Expand permissions'}
                                  onClick={() =>
                                    setExpandedEmail((prev) =>
                                      prev === account.shared_with ? null : account.shared_with
                                    )
                                  }
                                >
                                  <span
                                    className={`tw-inline-block tw-text-xs tw-transition-transform tw-duration-200 tw-ease-out ${open ? '-tw-rotate-180' : ''}`}
                                    aria-hidden
                                  >
                                    ▾
                                  </span>
                                </button>
                              </div>
                            </td>
                          </>
                        ) : null}
                      </tr>
                      {open && !isMobile ? (
                        <tr className="tw-bg-slate-50/50">
                          <td colSpan={activeColumns.length} className="tw-p-0">
                            <TeamMemberPermissionsPanel
                              email={account.shared_with}
                              accessPermission={account.access_permission}
                              active={open}
                              aiToolsFeatureEnabled={aiToolsFeatureEnabled}
                              integrationManagementEnabled={!readOnly}
                              layoutVariant="inline"
                              footerVariant="inline"
                              onAfterSave={() => setExpandedEmail(null)}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile: card list */}
        <div className="tw-space-y-3 tw-px-4 md:tw-hidden">
          <div className="tw-w-full tw-max-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-sm">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-3">
                <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-100 tw-text-sm tw-font-semibold tw-text-indigo-800">
                  {ownerInitial}
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-break-words tw-font-semibold tw-text-slate-900">{ownerTitle}</div>
                  <div className="tw-break-all tw-text-sm tw-text-slate-500">{ownerEmail}</div>
                </div>
              </div>
              <Badge pill bg="secondary" className="tw-shrink-0 tw-text-[10px] tw-font-medium">
                Owner
              </Badge>
            </div>
            {!readOnly ? (
              <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <PageAccessPill enabled={ownerPageStats.enabled} total={ownerPageStats.total} muted />
                {showAiToolsColumn && catalogLoaded ? (
                  <AiToolsPill enabled={ownerTools.enabled} total={ownerTools.total} muted />
                ) : null}
                <IntegrationManagePill allowed muted />
              </div>
            ) : null}
          </div>

          {acceptedMembers.map((account) => {
            if (!account) return null
            const merged = mergeAccessPermissionWithCanonical(account.access_permission)
            const pageStats = countMemberPageAccess(account.access_permission)
            const toolStats = countToggleableToolAccess(merged, availableToolsCatalog)
            const canManageIntegrations = canManageAllTeamIntegrations(account.access_permission)
            const displayName = account.shared_with
            const initial = displayName.trim().charAt(0).toUpperCase()
            const open = !readOnly && expandedEmail === account.shared_with
            const expandable = !readOnly

            return (
              <div
                key={account.shared_with}
                role={expandable ? 'button' : undefined}
                tabIndex={expandable ? 0 : undefined}
                aria-expanded={expandable ? open : undefined}
                onKeyDown={(e) => {
                  if (!expandable) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setExpandedEmail((prev) => (prev === account.shared_with ? null : account.shared_with))
                  }
                }}
                onClick={() => {
                  if (!expandable) return
                  setExpandedEmail((prev) => (prev === account.shared_with ? null : account.shared_with))
                }}
                className={`tw-w-full tw-max-w-full tw-rounded-xl tw-border tw-bg-white tw-p-4 tw-text-left tw-shadow-sm tw-transition-colors ${
                  open
                    ? 'tw-border-indigo-300 tw-ring-2 tw-ring-indigo-100'
                    : 'tw-border-slate-200 hover:tw-border-slate-300'
                } ${
                  expandable
                    ? 'tw-cursor-pointer tw-outline-none active:tw-bg-slate-50/80 focus-visible:tw-ring-2 focus-visible:tw-ring-indigo-400/50 focus-visible:tw-ring-offset-2'
                    : 'tw-cursor-default'
                }`}
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-3">
                    <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-100 tw-text-sm tw-font-semibold tw-text-indigo-800">
                      {initial}
                    </div>
                    <div className="tw-min-w-0 tw-flex-1">
                      <div className="tw-break-words tw-font-semibold tw-text-slate-900">{displayName}</div>
                      <div className="tw-break-all tw-text-sm tw-text-slate-500">{displayName}</div>
                    </div>
                  </div>
                  <div className="tw-flex tw-shrink-0 tw-items-start tw-gap-1">
                    <Badge pill bg="secondary" className="tw-text-[10px] tw-font-medium">
                      {resolveMemberOrgRole(account.shared_with, inOrgContext, rolesByEmail)}
                    </Badge>
                    {!readOnly ? (
                      <MobileMemberOverflowMenu
                        email={account.shared_with}
                        isEntrepreneur={isEntrepreneur}
                        seatAssigned={!!account.seat_assigned}
                        ownerEmail={teamOwnerEmail}
                        onRevoke={() => setRevokeConfirmEmail(account.shared_with)}
                        inOrgContext={inOrgContext}
                        isOrgOwner={isOrgOwner}
                        organizationId={organizationId}
                        rolesByEmail={rolesByEmail}
                        onTransfer={setTransferConfirmEmail}
                      />
                    ) : null}
                  </div>
                </div>
                {!readOnly ? (
                  <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <PageAccessPill enabled={pageStats.enabled} total={pageStats.total} muted />
                    {showAiToolsColumn ? (
                      catalogLoaded ? (
                        <AiToolsPill enabled={toolStats.enabled} total={toolStats.total} muted />
                      ) : (
                        <span className="tw-text-xs tw-text-slate-400">—</span>
                      )
                    ) : null}
                    <IntegrationManagePill allowed={canManageIntegrations} muted />
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </section>

      {sheetOpen && expandedAccount ? (
        <MemberPermissionsBottomSheet
          open={sheetOpen}
          memberEmail={expandedAccount.shared_with}
          onClose={() => setExpandedEmail(null)}
        >
          <TeamMemberPermissionsPanel
            email={expandedAccount.shared_with}
            accessPermission={expandedAccount.access_permission}
            active
            aiToolsFeatureEnabled={aiToolsFeatureEnabled}
            integrationManagementEnabled={!readOnly}
            layoutVariant="sheet"
            footerVariant="inline"
            onAfterSave={() => setExpandedEmail(null)}
          />
        </MemberPermissionsBottomSheet>
      ) : null}

      {pendingInvites.length > 0 ? (
        <section className="tw-min-w-0">
          <h3 className="tw-mb-3 tw-px-4 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-slate-500 tw-uppercase md:tw-px-6">
            Pending invites
          </h3>
          <div className="tw-px-4 md:tw-px-6">
            {/* Mobile: stacked cards so long emails and “Sent” copy are not squeezed by table columns */}
            <div className="tw-space-y-3 md:tw-hidden">
              {pendingInvites.map((account) => {
                if (!account) return null
                const email = account.shared_with
                const initial = email.trim().charAt(0).toUpperCase()
                const sent =
                  account.create_time != null
                    ? formatDistanceToNow(new Date(account.create_time), { addSuffix: true })
                    : '—'
                return (
                  <div
                    key={account.shared_with}
                    className="tw-w-full tw-max-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-sm"
                  >
                    {readOnly ? (
                      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                        <UserCell initial={initial} title={email} subtitle={email} />
                        <StatusBadge color="secondary" label="Pending" />
                      </div>
                    ) : (
                      <>
                        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
                          <div className="tw-min-w-0 tw-flex-1">
                            <div className="tw-mb-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
                              Invited email
                            </div>
                            <p className="tw-mb-0 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900">{email}</p>
                          </div>
                          <StatusBadge color="secondary" label="Pending" />
                        </div>
                        <dl className="tw-mb-0 tw-mt-4 tw-space-y-2 tw-text-sm">
                          <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-4">
                            <dt className="tw-mb-0 tw-shrink-0 tw-text-slate-500">Sent</dt>
                            <dd className="tw-mb-0 tw-min-w-0 tw-text-right tw-text-slate-700">{sent}</dd>
                          </div>
                          <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-4">
                            <dt className="tw-mb-0 tw-shrink-0 tw-text-slate-500">Role</dt>
                            <dd className="tw-mb-0 tw-text-slate-800">Member</dd>
                          </div>
                        </dl>
                        <RemoveButton
                          email={account.shared_with}
                          label="Cancel"
                          className="tw-mt-4 tw-w-full tw-min-h-[44px] tw-text-sm"
                        />
                      </>
                    )}
                  </div>
                )
              })}
            </div>

            <div className="tw-hidden tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-200 tw-text-sm tw-shadow-sm md:tw-block">
              <table className="tw-w-full tw-table-auto tw-border-collapse">
                <thead className="tw-border-b tw-bg-slate-50 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-600">
                  <tr>
                    {readOnly ? (
                      <>
                        <th className="tw-px-4 tw-py-3">User</th>
                        <th className="tw-px-4 tw-py-3">Role</th>
                      </>
                    ) : (
                      <>
                        <th className="tw-min-w-[12rem] tw-max-w-md tw-px-4 tw-py-3">Invited email</th>
                        <th className="tw-whitespace-nowrap tw-px-4 tw-py-3">Sent</th>
                        <th className="tw-whitespace-nowrap tw-px-4 tw-py-3">Role</th>
                        <th className="tw-w-28 tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-right">Actions</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.map((account, i) => {
                    if (!account) return null
                    const email = account.shared_with
                    const initial = email.trim().charAt(0).toUpperCase()
                    const sent =
                      account.create_time != null
                        ? formatDistanceToNow(new Date(account.create_time), { addSuffix: true })
                        : '—'
                    return (
                      <tr key={account.shared_with} className={i !== 0 ? `tw-border-t tw-border-slate-100` : ''}>
                        {readOnly ? (
                          <>
                            <td className="tw-px-4 tw-py-3 tw-align-middle">
                              <UserCell initial={initial} title={email} subtitle={email} />
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-slate-800">Pending</td>
                          </>
                        ) : (
                          <>
                            <td className="tw-max-w-md tw-px-4 tw-py-3 tw-align-middle">
                              <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-2">
                                <span className="tw-break-all tw-font-medium tw-text-slate-900">{account.shared_with}</span>
                                <StatusBadge color="secondary" label="Pending" />
                              </div>
                            </td>
                            <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-middle tw-text-slate-600">
                              {sent}
                            </td>
                            <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-middle tw-text-slate-800">
                              Member
                            </td>
                            <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-right">
                              <RemoveButton email={account.shared_with} label="Cancel" />
                            </td>
                          </>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {!readOnly ? (
        <Modal
          show={revokeConfirmEmail != null}
          onHide={closeRevokeModal}
          centered
          size="sm"
          backdrop={revokeLoading ? 'static' : true}
          keyboard={!revokeLoading}
        >
          <Modal.Header closeButton>
            <Modal.Title className="tw-text-base">Revoke access?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="tw-mb-0 tw-text-sm tw-text-slate-600">
              <span className="tw-font-medium tw-text-slate-900">{revokeConfirmEmail}</span> will lose access to
              this account. You can invite them again later if needed.
            </p>
          </Modal.Body>
          <Modal.Footer className="tw-flex tw-flex-col-reverse tw-gap-2 tw-border-0 tw-bg-white tw-pt-0 sm:tw-flex-row sm:tw-justify-end">
            <Button
              variant="secondary"
              size="sm"
              className="tw-w-full tw-min-h-[44px] sm:tw-min-h-0 sm:tw-w-auto"
              onClick={closeRevokeModal}
              disabled={revokeLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="tw-w-full tw-min-h-[44px] sm:tw-min-h-0 sm:tw-w-auto"
              onClick={handleConfirmRevoke}
              disabled={revokeLoading}
            >
              {revokeLoading ? 'Revoking...' : 'Revoke access'}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}

      {inOrgContext && isOrgOwner && organizationId ? (
        <Dialog
          open={transferConfirmEmail != null}
          onOpenChange={(open) => {
            if (!open && !transferLoading) closeTransferModal()
          }}
        >
          <DialogContent
            className="tw-gap-0 tw-overflow-hidden tw-p-0 sm:tw-max-w-md sm:tw-rounded-xl"
            onInteractOutside={(e) => {
              if (transferLoading) e.preventDefault()
            }}
            onEscapeKeyDown={(e) => {
              if (transferLoading) e.preventDefault()
            }}
            hideClose={transferLoading}
          >
            <div className="tw-border-b tw-border-slate-100 tw-bg-gradient-to-br tw-from-amber-50/80 tw-via-white tw-to-orange-50/40 tw-px-6 tw-pt-6 tw-pb-5">
              <DialogHeader className="tw-space-y-3 tw-text-left">
                <div className="tw-flex tw-items-start tw-gap-3">
                  <div className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-amber-500 tw-text-white tw-shadow-md tw-shadow-amber-200/80">
                    <ArrowRightLeft className="tw-h-5 tw-w-5" strokeWidth={2} />
                  </div>
                  <div className="tw-min-w-0 tw-flex-1 tw-pr-6">
                    <DialogTitle className="tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900">
                      Transfer ownership?
                    </DialogTitle>
                    <DialogDescription className="tw-mt-1.5 tw-text-sm tw-leading-relaxed tw-text-slate-600">
                      This changes who manages billing and organization settings.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
            </div>

            <div className="tw-space-y-4 tw-px-6 tw-py-5">
              <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50/70 tw-p-4">
                <div className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-100 tw-text-sm tw-font-semibold tw-text-indigo-800">
                  {(transferConfirmEmail || '?').trim().charAt(0).toUpperCase()}
                </div>
                <div className="tw-min-w-0">
                  <p className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">
                    New owner
                  </p>
                  <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900">
                    {transferConfirmEmail}
                  </p>
                </div>
              </div>

              <div className="tw-space-y-2.5 tw-rounded-xl tw-border tw-border-slate-100 tw-bg-white tw-px-4 tw-py-3.5">
                <p className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">
                  What happens next
                </p>
                <ul className="tw-space-y-2 tw-text-sm tw-leading-relaxed tw-text-slate-600">
                  <li className="tw-flex tw-gap-2.5">
                    <span className="tw-mt-1.5 tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-amber-400" />
                    <span>
                      <span className="tw-font-medium tw-text-slate-800">{transferConfirmEmail}</span>{' '}
                      becomes the organization owner.
                    </span>
                  </li>
                  <li className="tw-flex tw-gap-2.5">
                    <span className="tw-mt-1.5 tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-slate-300" />
                    <span>You become a Member and stay on the team.</span>
                  </li>
                  <li className="tw-flex tw-gap-2.5">
                    <span className="tw-mt-1.5 tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-slate-300" />
                    <span>The new owner will be asked to add their own payment method in Plan settings.</span>
                  </li>
                </ul>
              </div>
            </div>

            <DialogFooter className="tw-gap-2 tw-border-t tw-border-slate-100 tw-bg-slate-50/50 tw-px-6 tw-py-4 sm:tw-gap-3">
              <UiButton
                type="button"
                variant="outline"
                className="tw-w-full tw-min-h-[44px] sm:tw-min-h-10 sm:tw-w-auto"
                onClick={closeTransferModal}
                disabled={transferLoading}
              >
                Cancel
              </UiButton>
              <UiButton
                type="button"
                className={cn(
                  'tw-w-full tw-min-h-[44px] tw-bg-amber-500 tw-text-slate-900 hover:tw-bg-amber-600 focus-visible:tw-ring-amber-200 sm:tw-min-h-10 sm:tw-w-auto',
                  transferLoading && 'tw-opacity-80'
                )}
                onClick={() => void handleConfirmTransfer()}
                disabled={transferLoading}
              >
                {transferLoading ? (
                  <>
                    <Loader2 className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" />
                    Transferring…
                  </>
                ) : (
                  'Confirm transfer'
                )}
              </UiButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}
